import { memo } from "react";
import { Link } from "react-router-dom";
import {
  EnhancedSeoHead,
  SeoFunnelLink,
  generateArticleSchema,
  generateBreadcrumbSchema,
  combineSchemas,
} from "@appcaire/shared/seo";
import { ArrowRight, Building2, MapPin, Users } from "lucide-react";

const SOURCE_SITE = "nackahemtjanst.se";
const SOURCE_PATH = "/utforare/moderna-system";

const ModernaSystemForUtforare = memo(() => {
  const articleSchema = generateArticleSchema({
    headline: "Moderna system för hemtjänstutförare i Nacka",
    description:
      "Lokal guide för hemtjänstutförare i Nacka som vill förstå moderna planeringssystem, AI-schemaläggning och bättre kontinuitet.",
    url: `https://nackahemtjanst.se${SOURCE_PATH}`,
    datePublished: "2026-06-28",
    dateModified: "2026-06-28",
  });

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: "Hem", url: "https://nackahemtjanst.se/" },
    { name: "Utförare", url: "https://nackahemtjanst.se/utforare" },
    {
      name: "Moderna system",
      url: `https://nackahemtjanst.se${SOURCE_PATH}`,
    },
  ]);

  const combinedSchema = combineSchemas([articleSchema, breadcrumbSchema]);

  const links = [
    {
      href: "https://www.caire.se/losningar/privat-hemtjanst",
      targetPath: "/losningar/privat-hemtjanst",
      label: "Caire för privat hemtjänst",
      anchorCluster: "privat hemtjänst planeringssystem",
    },
    {
      href: "https://www.caire.se/produkt/funktioner/ai-schemalaggning",
      targetPath: "/produkt/funktioner/ai-schemalaggning",
      label: "AI-schemaläggning för hemtjänst",
      anchorCluster: "AI-schemaläggning för hemtjänst",
    },
    {
      href: "https://www.caire.se/kontakt",
      targetPath: "/kontakt",
      label: "Kontakta Caire",
      anchorCluster: "prata med Caire",
    },
  ] as const;

  return (
    <>
      <EnhancedSeoHead
        title="Moderna system för hemtjänstutförare i Nacka | Nacka Hemtjänst"
        description="Lokal guide för hemtjänstutförare i Nacka som vill förstå moderna planeringssystem, AI-schemaläggning och bättre kontinuitet."
        canonicalUrl={`https://nackahemtjanst.se${SOURCE_PATH}`}
        ogType="article"
        keywords={[
          "hemtjänstutförare Nacka",
          "moderna system hemtjänst",
          "AI-schemaläggning hemtjänst",
        ]}
        contentClassification="Local Provider Guide"
        targetAudience="hemtjänstutförare i Nacka"
        publishDate="2026-06-28T00:00:00+02:00"
        modifiedDate="2026-06-28T00:00:00+02:00"
        author="Nacka Hemtjänst"
        schemaMarkup={combinedSchema ?? undefined}
        entities={[{ type: "Product", name: "Caire" }]}
      />

      <section className="px-4 py-16 md:py-24 bg-gradient-to-b from-amber-50 via-white to-white">
        <div className="max-w-5xl mx-auto">
          <Link
            to="/utforare"
            className="text-sm font-semibold text-amber-700 hover:underline"
          >
            Utförare i Nacka
          </Link>
          <h1 className="mt-4 text-4xl md:text-5xl font-bold text-surface-900 tracking-tight max-w-3xl">
            Moderna system för hemtjänstutförare i Nacka
          </h1>
          <p className="mt-6 text-xl text-surface-600 leading-relaxed max-w-3xl">
            För lokala utförare handlar teknik inte om att ersätta omsorgen. Det
            handlar om att ge planerare bättre beslutsstöd, skydda kontinuiteten
            och minska onödig restid i en kommun med tydliga lokala
            förutsättningar.
          </p>
        </div>
      </section>

      <main className="bg-white">
        <section className="px-4 py-16">
          <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6">
            {[
              {
                icon: MapPin,
                title: "Lokala rutter",
                text: "Nacka ställer krav på geografisk förståelse: avstånd, broar, trafik och lokala områden påverkar planeringen.",
              },
              {
                icon: Users,
                title: "Kontinuitet",
                text: "Brukare och anhöriga märker när planeringen ger färre nya ansikten och bättre framförhållning.",
              },
              {
                icon: Building2,
                title: "Utförarens kontroll",
                text: "Systemet ska hjälpa verksamheten att fatta bättre beslut, inte ta över lokalt ansvar eller myndighetsprocesser.",
              },
            ].map((item) => (
              <article
                key={item.title}
                className="rounded-2xl border border-surface-200 bg-surface-50 p-6"
              >
                <item.icon className="w-8 h-8 text-amber-700 mb-4" />
                <h2 className="text-xl font-bold text-surface-900 mb-3">
                  {item.title}
                </h2>
                <p className="text-surface-600 leading-relaxed">{item.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="px-4 py-16 bg-surface-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-surface-900 mb-6">
              Relevanta Caire-sidor
            </h2>
            <div className="space-y-4">
              {links.map((link) => (
                <SeoFunnelLink
                  key={link.href}
                  href={link.href}
                  target="_blank"
                  rel="noreferrer"
                  tracking={{
                    sourceSite: SOURCE_SITE,
                    sourcePath: SOURCE_PATH,
                    targetPath: link.targetPath,
                    anchorCluster: link.anchorCluster,
                    ctaType: "editorial",
                  }}
                  className="flex items-center justify-between rounded-xl border border-surface-200 bg-white p-5 font-semibold text-surface-900 shadow-sm hover:border-amber-300 hover:bg-amber-50"
                >
                  {link.label}
                  <ArrowRight className="w-5 h-5 text-amber-700" />
                </SeoFunnelLink>
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
});

ModernaSystemForUtforare.displayName = "ModernaSystemForUtforare";
export default ModernaSystemForUtforare;
